import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress } from "@material-ui/core";

import { saveUserMonitorData } from "./userUtils";

interface DownloadTableButtonProps {
  downloadTableFunction: () => Promise<DataObject[]>;
  total: number;
  fileName: string;
}

interface DataObject {
  [key: string]: string | number;
}

export const DownloadTableButton = (props: DownloadTableButtonProps) => {
  const [downLoading, setDownLoading] = useState(false);
  const { t } = useTranslation();

  const handleDownloadTableButtonPressed = async (props: DownloadTableButtonProps) => {
    if (props.downloadTableFunction === undefined) {
      return;
    }
    if (props.total > 1000) {
      const message = t(
        `You are about to download ${props.total} rows of data. This may take approximately ${Math.ceil(props.total / 6000)} minutes and could impact other users. Do you want to proceed?`,
      );
      const isConfirmed = window.confirm(message);
      if (!isConfirmed) {
        return;
      }
    }

    setDownLoading(true);
    const startTime = new Date().getTime();
    const dataToDownload: DataObject[] = await props.downloadTableFunction();
    const consumedTime = new Date().getTime() - startTime;
    const csvString = convertToCSV(dataToDownload);
    saveUserMonitorData(
      "policyListDownload",
      `{rows:${props.total},bytes:${csvString.length},time:${consumedTime}}`,
    );
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    // Format the current date and time as DD_MM_YYYY_HH-MM-SS in local time
    const now = new Date();
    const formattedDate = now.toLocaleString("it-IT").replace(" ", "_").replace(",", "").replace(/:/g, "-");
    a.download = `${props.fileName}_${formattedDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setDownLoading(false);
  };

  const convertToCSV = (arrayOfObjects: DataObject[]) => {
    const headers: string[] = Object.keys(arrayOfObjects[0]);

    // Make it Excel friendly for Italian users
    const allExceptHeaders: string = arrayOfObjects.map((row) => formatRow(row, headers)).join("\n");

    return `${headers.join(";")}\n${allExceptHeaders}`;
  };

  const formatRow = (row: DataObject, headers: string[]): string => {
    return headers.map((header) => formatValue(row[header])).join(";");
  };

  const formatValue = (value: string | number | null): string => {
    if (typeof value === "number") {
      return value.toString().replace(".", ",");
    }
    if (typeof value === "string") {
      if (value.includes("/")) {
        // We use this to force Excel to treat the value as a string.
        // However it may break the date format in some cases, so we should avoid it when possible.
        return `="${value}"`;
      }
      if (value.includes("T00:00:00.000Z")) {
        return value.replace("T00:00:00.000Z", "");
      }
      return value; // to be discussed if =value or 'value is useful here, to be more Excel-friendly.
    }
    return "";
  };

  return (
    <Box ml={1}>
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        onClick={() => handleDownloadTableButtonPressed(props)}
        disabled={downLoading}
      >
        {downLoading && (
          <Box mr={1}>
            <CircularProgress size={12}></CircularProgress>
          </Box>
        )}
        {t("DOWNLOAD")}
      </Button>
    </Box>
  );
};
